import { Button, Header, Stack, StackItem } from '@asgard/ui-library';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FormCheckbox } from 'components/form/FormCheckbox';
import { FormPhone } from 'components/form/FormPhone';
import { FormText } from 'components/form/FormText/FormText';
import { FormTextarea } from 'components/form/FormTextarea';
import { HashAnchor } from 'components/HashAnchor';
import { PageContentContainer } from 'components/layout/Page.styled';
import HtmlTrans from 'components/shared/HtmlTrans';
import { LinkText } from 'components/shared/LinkText';
import { PHONE_NUMBER_DEFAULT_COUNTRY } from 'features/portal/v2/contact/basic-info/ContactBasicInfo.constants';
import { ManfreditesStats } from 'model/manfredites-stats/model.manfredites-stats';
import { Trans } from 'next-i18next';
import { FormProvider } from 'react-hook-form';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { useCompaniesForm } from './YourOfferSalaryFormContact.hooks';
import { Form } from './YourOfferSalaryFormContact.styled';

const YourOfferSalaryFormContact: React.FC<{ data: ManfreditesStats }> = ({
  data
}) => {
  const {
    t,
    formMethods,
    locale,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  } = useCompaniesForm();

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = formMethods;

  return (
    <Form.Root>
      <PageContentContainer containerSize="xl">
        <HashAnchor name="yourOfferSalaryForm" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Form.Intro>
            <Header editorial as="h2" size="5xl">
              {data.hasEnoughManfredites
                ? t('your-offer-salary.form-contact.title')
                : t('your-offer-salary.form-contact.title-no-data')}
            </Header>

            <HtmlTrans
              ns="your-offer-salary"
              i18nKey={
                data.hasEnoughManfredites
                  ? 'your-offer-salary.form-contact.desc'
                  : 'your-offer-salary.form-contact.desc-no-data'
              }
            />
          </Form.Intro>

          <FormProvider {...formMethods}>
            <Stack direction="column" gap="3xl">
              <Stack>
                <StackItem className="half">
                  <FormText
                    name="name"
                    formGroup={{
                      label: t('your-offer-salary.form-contact.name')
                    }}
                    input={{
                      placeholder: t(
                        'your-offer-salary.form-contact.name_placeholder'
                      ),
                      onFocus: onFormFieldFocus
                    }}
                  />
                </StackItem>

                <StackItem className="half">
                  <FormText
                    name="company"
                    formGroup={{
                      label: t('your-offer-salary.form-contact.company')
                    }}
                    input={{
                      placeholder: t(
                        'your-offer-salary.form-contact.company_placeholder'
                      ),
                      onFocus: onFormFieldFocus
                    }}
                  />
                </StackItem>
              </Stack>

              <Stack>
                <StackItem className="half">
                  <FormText
                    name="email"
                    formGroup={{
                      label: t('your-offer-salary.form-contact.email')
                    }}
                    input={{
                      placeholder: t(
                        'your-offer-salary.form-contact.email_placeholder'
                      ),
                      onFocus: onFormFieldFocus
                    }}
                  />
                </StackItem>
                <StackItem className="half phoneNumber">
                  <FormPhone
                    name="contactPhone"
                    formGroup={{
                      label: t('your-offer-salary.form-contact.phone')
                    }}
                    input={{
                      defaultCountry: PHONE_NUMBER_DEFAULT_COUNTRY,
                      onFocus: onFormFieldFocus
                    }}
                  />
                </StackItem>
              </Stack>

              <FormTextarea
                name="description"
                formGroup={{
                  label: t('your-offer-salary.form-contact.description')
                }}
                input={{
                  placeholder: t(
                    'your-offer-salary.form-contact.description_placeholder'
                  ),
                  onFocus: onFormFieldFocus,
                  rows: 3
                }}
              />

              <Stack direction="column" gap="s">
                <FormCheckbox
                  name="terms"
                  input={{
                    onFocus: onFormFieldFocus,
                    label: (
                      <Trans
                        ns={'your-offer-salary'}
                        i18nKey={'your-offer-salary.form-contact.terms'}
                        components={{
                          privatePolicy: (
                            <LinkText
                              href={getAppUrl(APP_URLS.privacyPolicy, locale)}
                            />
                          ),
                          terms: (
                            <LinkText
                              href={getAppUrl(APP_URLS.termsOfUse, locale)}
                            />
                          )
                        }}
                      />
                    )
                  }}
                />

                <FormCheckbox
                  name="isSubscribeToNewsletter"
                  input={{
                    onFocus: onFormFieldFocus,
                    label: (
                      <Trans
                        ns={'your-offer-salary'}
                        i18nKey={'your-offer-salary.form-contact.newsletter'}
                        components={{
                          newsletter: (
                            <a
                              href="https://none.clickacm.com/show/qgJ2lZXPBBizIfJi3pmS/"
                              target="_blank"
                            />
                          )
                        }}
                      ></Trans>
                    )
                  }}
                />
              </Stack>

              <StackItem>
                <Button
                  schema="brand1"
                  variant="hard"
                  label={t('your-offer-salary.form-contact.submit')}
                  startIcon={faCheckCircle}
                  type="submit"
                  disabled={!isTermsAccepted}
                  isLoading={isSubmitting}
                  loadingText={t(
                    'your-offer-salary.form-contact.submit_in-progress'
                  )}
                />
              </StackItem>
            </Stack>
          </FormProvider>
        </form>
      </PageContentContainer>
    </Form.Root>
  );
};

export default YourOfferSalaryFormContact;
